/**
 * Permisos de un usuario (spec 01, enmienda 2026-10-09): parte de la plantilla de su rol y la dueña le da o le
 * quita permisos uno por uno. Se guarda solo lo que difiere de la plantilla; rige de inmediato.
 */
import { NIVELES, ROL_ETIQUETA, type Nivel, type Permiso, type PermisosUsuarioDTO, type Rol } from '@mg/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { EstadoQuery } from '@/componentes/estados.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input, Select } from '@/componentes/ui/input.tsx';
import { get, mensajeDeError, put } from '@/lib/api.ts';

const NIVEL_TEXTO: Record<Nivel, string> = { total: 'Sí (total)', propone: 'Propone', lectura: 'Solo ver' };
const valor = (n: Nivel | null) => n ?? 'no';

export function DialogoPermisosUsuario({ usuarioId, cerrar }: { usuarioId: string | null; cerrar: () => void }) {
  const qc = useQueryClient();
  const datos = useQuery({ queryKey: ['usuarios', 'permisos', usuarioId], queryFn: () => get<PermisosUsuarioDTO>(`/usuarios/${usuarioId}/permisos`), enabled: !!usuarioId });
  /** Cambios locales: permiso → nivel elegido (null = sin acceso). Lo no tocado conserva lo que ya tenía. */
  const [cambios, setCambios] = useState<Partial<Record<Permiso, Nivel | null>>>({});
  const [q, setQ] = useState('');
  const guardar = useMutation({
    mutationFn: (permisos: { permiso: Permiso; nivel: Nivel | null }[]) => put<PermisosUsuarioDTO>(`/usuarios/${usuarioId}/permisos`, { permisos }),
    onSuccess: async (r) => {
      const propios = r.filas.filter((f) => f.propio !== undefined).length;
      toast.success(`Permisos de ${r.nombre} guardados`, { description: propios === 0 ? 'Igual a la plantilla de su rol.' : `${propios} permiso(s) distinto(s) a la plantilla. Rigen de inmediato.` });
      setCambios({});
      await qc.invalidateQueries({ queryKey: ['usuarios'] });
      cerrar();
    },
    onError: (e) => toast.error(mensajeDeError(e)),
  });
  const cerrarYLimpiar = () => {
    setCambios({});
    setQ('');
    cerrar();
  };

  return (
    <Dialog open={!!usuarioId} onOpenChange={(o) => !o && cerrarYLimpiar()}>
      <DialogContent className="max-w-3xl" data-testid="dialogo-permisos">
        <DialogHeader>
          <div className="flex items-center gap-1"><DialogTitle>Permisos de {datos.data?.nombre ?? '…'}</DialogTitle><AyudaVentana tema="configuracion.permisos" /></div>
          <DialogDescription>
            Rol: {datos.data ? ROL_ETIQUETA[datos.data.rol as Rol] : ''}. Cada fila parte de la plantilla del rol; cambie solo lo que este usuario necesita de más o de menos.
          </DialogDescription>
        </DialogHeader>
        <EstadoQuery cargando={datos.isLoading} error={datos.error} datos={datos.data} reintentar={() => void datos.refetch()}>
          {(d) => {
            const esDuena = d.rol === 'superadmin';
            const elegido = (permiso: Permiso, efectivo: Nivel | null) => (permiso in cambios ? (cambios[permiso] ?? null) : efectivo);
            const filas = d.filas.filter((f) => !q.trim() || f.etiqueta.toLowerCase().includes(q.trim().toLowerCase()) || f.permiso.includes(q.trim().toLowerCase()));
            const distintos = d.filas.filter((f) => elegido(f.permiso, f.efectivo) !== f.plantilla).length;
            return (
              <>
                <div className="flex items-center gap-2">
                  <Input placeholder="Buscar permiso…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9" data-testid="permisos-buscar" />
                  <Badge variant={distintos > 0 ? 'pendiente' : 'neutro'} data-testid="permisos-distintos">{distintos} distinto(s) a la plantilla</Badge>
                  {!esDuena ? <Button size="sm" variant="ghost" onClick={() => setCambios(Object.fromEntries(d.filas.map((f) => [f.permiso, f.plantilla])))} data-testid="permisos-plantilla">Volver a la plantilla</Button> : null}
                </div>
                <div className="max-h-[52vh] overflow-y-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-muted text-[11px] uppercase text-muted-foreground">
                      <tr><th className="px-3 py-1.5 text-left">Permiso</th><th className="px-2 py-1.5 text-left">Plantilla del rol</th><th className="px-2 py-1.5 text-left">Este usuario</th></tr>
                    </thead>
                    <tbody>
                      {filas.map((f) => {
                        const actual = elegido(f.permiso, f.efectivo);
                        const distinto = actual !== f.plantilla;
                        return (
                          <tr key={f.permiso} className={`border-t ${distinto ? 'bg-amber-50' : ''}`} data-testid={`permiso-${f.permiso}`}>
                            <td className="px-3 py-1.5">{f.etiqueta}<span className="block text-[10px] text-muted-foreground">{f.permiso}</span></td>
                            <td className="px-2 py-1.5 text-muted-foreground">{f.plantilla ? NIVEL_TEXTO[f.plantilla] : 'No'}</td>
                            <td className="px-2 py-1.5">
                              <Select
                                value={valor(actual)}
                                disabled={esDuena}
                                onChange={(e) => setCambios((c) => ({ ...c, [f.permiso]: e.target.value === 'no' ? null : (e.target.value as Nivel) }))}
                                className="h-8 w-36"
                                aria-label={f.etiqueta}
                                data-testid={`permiso-nivel-${f.permiso}`}
                              >
                                <option value="no">No</option>
                                {NIVELES.map((n) => <option key={n} value={n}>{NIVEL_TEXTO[n]}</option>)}
                              </Select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {esDuena ? <p className="text-xs text-muted-foreground">La dueña siempre tiene todos los permisos: no se editan.</p> : null}
                <DialogFooter>
                  <Button variant="outline" onClick={cerrarYLimpiar}>Cancelar</Button>
                  <Button
                    disabled={esDuena}
                    cargando={guardar.isPending}
                    onClick={() => guardar.mutate(d.filas.map((f) => ({ permiso: f.permiso, nivel: elegido(f.permiso, f.efectivo) })))}
                    data-testid="permisos-guardar"
                  >
                    Guardar permisos
                  </Button>
                </DialogFooter>
              </>
            );
          }}
        </EstadoQuery>
      </DialogContent>
    </Dialog>
  );
}
